import { useToast } from '@/hooks/use-toast';

/**
 * Thin wrapper around the shadcn toast implementation that mimics the legacy
 * customToast API (success, error, loading, promise).
 */
export const useToastNotifications = () => {
  const { toast } = useToast();

  const showToast = ({ title, description, variant = 'default', ...rest }) =>
    toast({ title, description, variant, ...rest });

  const success = (message, options = {}) =>
    showToast({ title: message, variant: 'success', ...options });

  // A refusal is not a failure. "You cannot do this yet" — an unmet gate, an
  // incomplete form — reads as a warning in the Opus toast design; red is
  // reserved for something actually going wrong. Callers that mean "the request
  // broke" should use `error`.
  const warning = (message, options = {}) =>
    showToast({ title: message, variant: 'warning', ...options });

  const error = (message, options = {}) =>
    showToast({
      title: message,
      variant: 'error',
      ...options,
    });

  const loading = (message, options = {}) =>
    showToast({
      title: message,
      className: 'animate-pulse',
      ...options,
    });

  /**
   * Mimics the behaviour of customToast.promise. Accepts a promise factory or
   * promise and updates the same toast instance as the operation resolves.
   */
  const promise = async (
    promiseOrFn,
    {
      pending = 'Processing…',
      success: successMessage = 'Completed',
      error: errorMessage = 'Something went wrong',
      successOptions = {},
      errorOptions = {},
    } = {}
  ) => {
    const operation =
      typeof promiseOrFn === 'function' ? promiseOrFn() : promiseOrFn;

    const toastRef = toast({ title: pending });
    try {
      const result = await operation;
      toastRef.update({
        title: successMessage,
        variant: successOptions.variant ?? 'default',
        ...successOptions,
      });
      return result;
    } catch (err) {
      toastRef.update({
        title: errorMessage,
        variant: errorOptions.variant ?? 'destructive',
        ...errorOptions,
      });
      throw err;
    }
  };

  return {
    toast,
    success,
    warning,
    error,
    loading,
    promise,
  };
};

export default useToastNotifications;
